import { InterviewImportDecisionReason } from "../../graphql";

type Reason = {
  shortDescription: string;
  longDescription: string;
};

export const enabledReasons: Record<string, Reason> = {
  [InterviewImportDecisionReason.Manual]: {
    shortDescription: "Manual scheduling",
    longDescription:
      "Recording was enabled because the interview was manually scheduled from the homepage.",
  },
  [InterviewImportDecisionReason.Email]: {
    shortDescription: "Recorder invited",
    longDescription:
      "Recording was enabled by inviting recorder@brighthire.ai to a calendar event.",
  },
  [InterviewImportDecisionReason.Calendar]: {
    shortDescription: "Contains #brighthire",
    longDescription:
      "Recording was enabled by adding #brighthire to a synced calendar event.",
  },
  [InterviewImportDecisionReason.Position]: {
    shortDescription: "Join by Position",
    longDescription:
      "Recording was enabled because Join by Position is enabled.",
  },
  [InterviewImportDecisionReason.HomePage]: {
    shortDescription: "Manually added from Homepage",
    longDescription:
      "Recording was enabled by clicking Add BrightHire from the homepage.",
  },
  [InterviewImportDecisionReason.CalendarKeyword]: {
    shortDescription: "Keyword matched",
    longDescription:
      "Recording was enabled because a configured recording keyword was found in the calendar event body or title.",
  },
  [InterviewImportDecisionReason.InterviewAssistant]: {
    shortDescription: "Manually added from IA",
    longDescription:
      "User clicked to start the interview from the Interview Assistant.",
  },
  [InterviewImportDecisionReason.VirtualOnsiteSplit]: {
    shortDescription: "Virtual onsite",
    longDescription:
      "Recording enabled during a virtual onsite using the Interview Assistant.",
  },
  [InterviewImportDecisionReason.Acuity]: {
    shortDescription: "Acuity scheduled",
    longDescription:
      "Recording enabled because it was scheduled via Acuity and the Acuity integration is enabled.",
  },
  [InterviewImportDecisionReason.UserAllMyInterviews]: {
    shortDescription: "Record all enabled",
    longDescription:
      "Recording enabled because the user has record all interviews enabled.",
  },
  [InterviewImportDecisionReason.UserOnlyInterviewerMyInterviews]: {
    shortDescription: "Record mine enabled",
    longDescription:
      "Recording enabled because the user has record my interviews enabled.",
  },
  [InterviewImportDecisionReason.ApiOverride]: {
    shortDescription: "API enabled",
    longDescription: "Recording enabled via API.",
  },
  [InterviewImportDecisionReason.AdminForceImport]: {
    shortDescription: "Force enabled",
    longDescription: "Recording was force enabled by support or a Site Admin.",
  },
  [InterviewImportDecisionReason.InPersonInterview]: {
    shortDescription: "In-person",
    longDescription:
      "Recording enabled because the interviewer recorded an in person interview.",
  },
};

export const disabledReasons: Record<string, Reason> = {
  [InterviewImportDecisionReason.NoActiveBhUser]: {
    shortDescription: "No signed-in user",
    longDescription:
      "Recording is disabled because no active BrightHire user exists as an interviewer and the setting to require sign up before recording is enabled.",
  },
  [InterviewImportDecisionReason.NoBhUser]: {
    shortDescription: "No active user",
    longDescription:
      "Recording is disabled because no BrightHire user exists as an interviewer.",
  },
  [InterviewImportDecisionReason.PositionTurnedOff]: {
    shortDescription: "JBP off",
    longDescription:
      "Recording disabled because Join by Position was turned off for this position.",
  },
  [InterviewImportDecisionReason.ApiOverrideTurnedOff]: {
    shortDescription: "API disabled",
    longDescription: "Recording disabled via API.",
  },
  [InterviewImportDecisionReason.AdminForcePrevent]: {
    shortDescription: "Force disabled",
    longDescription: "Recording was force disabled by support or a Site Admin.",
  },
  [InterviewImportDecisionReason.NoWhitelistUser]: {
    shortDescription: "User not on recording allowlist",
    longDescription:
      "Recording disabled because the user is not on the recording whitelist.",
  },
  [InterviewImportDecisionReason.CalendarKeywordTurnedOff]: {
    shortDescription: "Keyword removed",
    longDescription:
      "Recording disabled because the user removed a recording keyword.",
  },
  [InterviewImportDecisionReason.UserLevelImportRuleTurnedOff]: {
    shortDescription: "User import turned off",
    longDescription:
      "Recording disabled because the user turned off import all interviews or import my interviews.",
  },
  [InterviewImportDecisionReason.NoDecision]: {
    shortDescription: "Not configured",
    longDescription:
      "Recording disabled because the interview is not configured to be recorded.",
  },
  [InterviewImportDecisionReason.Undo]: {
    shortDescription: "Recording disabled by system",
    longDescription: "Recording was undone by the system.",
  },
  [InterviewImportDecisionReason.Unknown]: {
    shortDescription: "Unknown",
    longDescription:
      "No import reason recorded — likely from before tracking was implemented.",
  },
};

export const optOutReasons: Record<string, Reason> = {
  [InterviewImportDecisionReason.Exclude]: {
    shortDescription: "Interviewer excluded",
    longDescription:
      "Recording disabled because an interviewer is on the exclude list.",
  },
  [InterviewImportDecisionReason.OptOutInterviewer]: {
    shortDescription: "Interviewer opt-out",
    longDescription: "Recording disabled because an interviewer opted out.",
  },
  [InterviewImportDecisionReason.OptOutCandidate]: {
    shortDescription: "Candidate opt-out",
    longDescription:
      "Recording disabled because the candidate opted out of all recordings.",
  },
  [InterviewImportDecisionReason.SomeoneOptedOut]: {
    shortDescription: "Recording cancelled",
    longDescription:
      "Recording disabled because the candidate or interviewer disabled recording.",
  },
  [InterviewImportDecisionReason.ExcludeStage]: {
    shortDescription: "Stage exclusion",
    longDescription:
      "Recording disabled because this interview stage is excluded from recording.",
  },
};
